import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Badge, type BadgeProps } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { Card } from '@/design-system/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/design-system/dropdown-menu';
import { IconButton } from '@/design-system/icon-button';
import { Paginator } from '@/design-system/pagination';
import { SearchField } from '@/design-system/search-field';
import { Spinner } from '@/design-system/spinner';
import { TableData, TableDataColumn } from '@/design-system/table-data';
import { Tabs, TabsCount, TabsList, TabsTrigger } from '@/design-system/tabs';
import { toast } from '@/design-system/toaster';
import { Pitstop, getListPitstop } from '@/services/api/pitstops';
import {
  InboundItem,
  InboundStatusFilter,
  ItemCounts,
  getInboundItems,
  getItemCounts,
  markException,
  readItemCount,
} from '@/services/api/inbound';
import {
  ITEM_STATUS_LABELS,
  isDamaged,
  type ItemStatus,
} from '@/services/api/status';

import ScanPanel from './components/ScanPanel';
import InboundFilterPanel, {
  EMPTY_INBOUND_FILTERS,
  DatePreset,
  InboundFilterValue,
  countActiveInboundFilters,
  resolvePresetRange,
} from './components/InboundFilterPanel';
import {
  useUrlParam,
  useUrlNumber,
  useUrlPatch,
  useUrlSearch,
} from '@/hooks/urlState';

// ── Constants ────────────────────────────────────────────────────────────────

/**
 * Tab keys. All but `DAMAGED` are real `status=` values (v2); `DAMAGED` is the
 * odd one out — v2 turned damage into the `condition` attribute, so that tab
 * filters `condition=DAMAGED` across every status instead.
 */
type StatusFilter = 'ALL' | InboundStatusFilter | 'DAMAGED';

const STATUS_TABS: { key: StatusFilter; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'CREATED', label: 'Created' },
  { key: 'HUB_SCANNED', label: 'Scanned' },
  { key: 'HANDOVER_SCANNED', label: 'Handover scanned' },
  // Out with a rider. Reachable here because a failed attempt comes back to the
  // hub and is re-scanned — the item stays IN_DELIVERY until it does.
  { key: 'IN_DELIVERY', label: 'In delivery' },
  { key: 'IN_RETURN', label: 'In return' },
  { key: 'RETURNED_TO_HUB', label: 'Returned to hub' },
  { key: 'LOST', label: 'Lost' },
  { key: 'DAMAGED', label: 'Damaged' },
];

const STATUS_KEYS = STATUS_TABS.map((t) => t.key) as readonly StatusFilter[];
// Switching a status tab clears search + page, keeps the filter-popover filters.
const STATUS_TAB_RESET = ['q'] as const;
const DATE_PRESET_KEYS: readonly DatePreset[] = [
  'today',
  'yesterday',
  'last_7_days',
  'last_30_days',
];

// URL param keys for the applied filter object (draft stays in local state).
const FILTER_PARAMS = {
  hubId: 'hubId',
  clientId: 'client',
  datePreset: 'preset',
  createdFrom: 'from',
  createdTo: 'to',
} as const;

// Inbound says "Scanned" where the shared vocabulary says "Hub scanned" — this
// page is *at* the hub, so the qualifier is noise.
const ITEM_STATUS_LABEL: Partial<Record<ItemStatus, string>> = {
  ...ITEM_STATUS_LABELS,
  HUB_SCANNED: 'Scanned',
};

type BadgeStatus = NonNullable<BadgeProps['status']>;

const ITEM_STATUS_TONE: Partial<Record<ItemStatus, BadgeStatus>> = {
  CREATED: 'neutral',
  HUB_SCANNED: 'success',
  // Not a tab here (the hub floor stops at handover) but a row can still carry
  // it — e.g. an item committed to a dispatch while this list was open.
  DISPATCHED: 'feature',
  HANDOVER_SCANNED: 'verified',
  IN_DELIVERY: 'away',
  COMPLETED: 'success',
  IN_RETURN: 'warning',
  RETURNED_TO_HUB: 'error',
  RETURNED_TO_CLIENT: 'error',
  CANCELLED: 'neutral',
  LOST: 'error',
  // Legacy (pre-migration rows)
  DELIVERED: 'success',
  RETURNED: 'error',
  DAMAGED: 'warning',
};

/** The exception endpoint only accepts hub-side items. */
const FLAGGABLE_STATUSES: readonly ItemStatus[] = ['CREATED', 'HUB_SCANNED'];

const DEFAULT_LIMIT = 20;

// ── Page ─────────────────────────────────────────────────────────────────────

const InboundPage: React.FC = () => {
  const [hubs, setHubs] = useState<Pitstop[]>([]);
  const [items, setItems] = useState<InboundItem[]>([]);
  const [total, setTotal] = useState(0);
  // Status tab / filters / search / page are URL-driven (shareable, back-button
  // aware). No pageSize on this page. Tab uses push history; tab/search/filter
  // changes drop `page` in the same URL write.
  const [page, setPage] = useUrlNumber('page', 1, { min: 1 });
  const [initialLoading, setInitialLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);

  // Applied filters derived from the URL; draft stays local for the popover.
  const { searchParams, patch: patchUrl } = useUrlPatch();
  const filters: InboundFilterValue = useMemo(() => {
    const preset = searchParams.get(FILTER_PARAMS.datePreset);
    return {
      hubId: searchParams.get(FILTER_PARAMS.hubId) ?? '',
      clientId: searchParams.get(FILTER_PARAMS.clientId) ?? '',
      datePreset: DATE_PRESET_KEYS.includes(preset as DatePreset)
        ? (preset as DatePreset)
        : '',
      createdFrom: searchParams.get(FILTER_PARAMS.createdFrom) ?? '',
      createdTo: searchParams.get(FILTER_PARAMS.createdTo) ?? '',
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);
  const [filterDraft, setFilterDraft] = useState<InboundFilterValue>(
    EMPTY_INBOUND_FILTERS
  );
  const [filterOpen, setFilterOpen] = useState(false);

  const [statusFilter, setStatusFilter] = useUrlParam<StatusFilter>(
    'status',
    'ALL',
    {
      allowed: STATUS_KEYS,
      resetPage: true,
      resetParams: STATUS_TAB_RESET,
      history: 'push',
    }
  );
  // URL-backed search; the box resyncs on external URL changes (status-tab
  // switch drops `q`, Back/forward).
  const {
    value: search,
    setValue: setSearch,
    committed: debouncedSearch,
  } = useUrlSearch('q', { resetPage: true, delay: 500 });

  // Seeded with the canonical v2 key names (`verifiedByHub` counts HUB_SCANNED —
  // see ItemCounts). Only visible before the first counts response lands.
  const [counts, setCounts] = useState<ItemCounts>({
    all: 0,
    created: 0,
    verifiedByHub: 0,
  });

  const [scanOpen, setScanOpen] = useState(false);
  const [exceptionInFlight, setExceptionInFlight] = useState<string | null>(
    null
  );

  const isMounted = useRef(true);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchHubs = useCallback(async () => {
    try {
      const res = await getListPitstop({
        type: 'HUB',
        hasStaff: true,
        page: '1',
        size: '100',
      });
      if (isMounted.current) setHubs(res.data ?? []);
    } catch (_) {
      // Hub list failing shouldn't block the page
    }
  }, []);

  const fetchItems = useCallback(
    async (silent = false) => {
      if (!silent) setTableLoading(true);
      try {
        const hubId = filters.hubId ? Number(filters.hubId) : undefined;
        const clientId = filters.clientId
          ? Number(filters.clientId)
          : undefined;
        const presetRange = resolvePresetRange(filters.datePreset);
        const createdFrom =
          (presetRange?.from ?? filters.createdFrom) || undefined;
        const createdTo = (presetRange?.to ?? filters.createdTo) || undefined;

        // The Damaged tab is a `condition` filter, not a status — a damaged
        // parcel still has a real custody status in v2.
        const isDamagedTab = statusFilter === 'DAMAGED';

        const res = await getInboundItems({
          hub_id: hubId,
          client_id: clientId,
          created_from: createdFrom,
          created_to: createdTo,
          status: isDamagedTab ? 'ALL' : statusFilter,
          condition: isDamagedTab ? 'DAMAGED' : undefined,
          search: debouncedSearch || undefined,
          page,
          limit: DEFAULT_LIMIT,
        });
        if (isMounted.current) {
          setItems(res.items ?? []);
          setTotal(res.total ?? 0);
        }
      } catch (_) {
        // Silently ignore poll errors
      } finally {
        if (!silent && isMounted.current) {
          setTableLoading(false);
          setInitialLoading(false);
        }
      }
    },
    [filters, statusFilter, debouncedSearch, page]
  );

  const fetchCounts = useCallback(async () => {
    try {
      const hubId = filters.hubId ? Number(filters.hubId) : undefined;
      const clientId = filters.clientId ? Number(filters.clientId) : undefined;
      const presetRange = resolvePresetRange(filters.datePreset);
      const createdFrom =
        (presetRange?.from ?? filters.createdFrom) || undefined;
      const createdTo = (presetRange?.to ?? filters.createdTo) || undefined;

      const res = await getItemCounts({
        hub_id: hubId,
        client_id: clientId,
        created_from: createdFrom,
        created_to: createdTo,
        search: debouncedSearch || undefined,
      });
      if (isMounted.current) setCounts(res);
    } catch (_) {
      // Non-blocking — tabs still work with stale counts
    }
  }, [filters, debouncedSearch]);

  // Initial load
  useEffect(() => {
    isMounted.current = true;
    fetchHubs();
    return () => {
      isMounted.current = false;
    };
  }, [fetchHubs]);

  // Items + counts: load on every input change …
  useEffect(() => {
    fetchItems(false);
    fetchCounts();
  }, [fetchItems, fetchCounts]);

  // No background polling (it kept hitting the API every 5 s for every open
  // tab): ops refresh by hand, like the Shipments menu.
  const handleRefresh = () => {
    fetchItems(true);
    fetchCounts();
  };

  // "/" focuses the search box
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (
        e.key === '/' &&
        document.activeElement?.tagName !== 'INPUT' &&
        document.activeElement?.tagName !== 'TEXTAREA'
      ) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Filter panel handlers — seed the draft from applied filters on open.
  const handleFilterOpenChange = (open: boolean) => {
    if (open) setFilterDraft(filters);
    setFilterOpen(open);
  };

  // Write every filter field + drop `page` in ONE URL patch (atomic).
  const writeFilters = (v: InboundFilterValue) => {
    patchUrl((params) => {
      const setOrDelete = (key: string, value: string) => {
        if (value) params.set(key, value);
        else params.delete(key);
      };
      setOrDelete(FILTER_PARAMS.hubId, v.hubId);
      setOrDelete(FILTER_PARAMS.clientId, v.clientId);
      setOrDelete(FILTER_PARAMS.datePreset, v.datePreset);
      setOrDelete(FILTER_PARAMS.createdFrom, v.createdFrom);
      setOrDelete(FILTER_PARAMS.createdTo, v.createdTo);
      params.delete('page');
    });
  };

  const applyFilter = () => {
    writeFilters(filterDraft);
    setFilterOpen(false);
  };
  const resetFilter = () => {
    setFilterDraft(EMPTY_INBOUND_FILTERS);
    writeFilters(EMPTY_INBOUND_FILTERS);
    setFilterOpen(false);
  };

  const handleScanSuccess = useCallback(() => {
    fetchItems(true);
    fetchCounts();
  }, [fetchItems, fetchCounts]);

  const handleException = useCallback(
    async (item: InboundItem, action: 'LOST' | 'DAMAGED' | 'REVERT') => {
      setExceptionInFlight(item.id);
      try {
        await markException(item.id, { action });
        const labels: Record<string, string> = {
          LOST: 'marked as Lost',
          // v2: this sets `condition`, it does not move the item's status.
          DAMAGED: 'flagged as Damaged',
          // Un-losing moves the status back; clearing damage only drops the flag.
          REVERT:
            item.status === 'LOST'
              ? item.scannedAt
                ? 'reverted to Scanned'
                : 'reverted to Created'
              : 'damage flag cleared',
        };
        toast.success('Item updated', {
          description: `${item.itemName} ${labels[action]}`,
        });
        fetchItems(true);
        fetchCounts();
      } catch (err: any) {
        toast.error('Update failed', {
          description: err?.message ?? 'Could not update item',
        });
      } finally {
        setExceptionInFlight(null);
      }
    },
    [fetchItems, fetchCounts]
  );

  // ── Columns ────────────────────────────────────────────────────────────────

  const columns: TableDataColumn<InboundItem>[] = [
    {
      key: 'itemName',
      title: 'Package Name',
      render: (_v, row) => (
        <span
          className="font-mono text-[12px] tabular text-text-strong-950"
          title={row.itemName}
        >
          {row.itemName.length > 28
            ? row.itemName.slice(0, 28) + '…'
            : row.itemName}
        </span>
      ),
    },
    {
      key: 'koli',
      title: 'Koli',
      align: 'right',
      // Physical boxes the hub should count for this item. Older BE builds
      // don't emit it — show a dash rather than a made-up 1.
      render: (_v, row) =>
        row.koli != null ? (
          <span className="font-mono text-[12px] tabular-nums text-text-strong-950">
            {row.koli}
          </span>
        ) : (
          <span className="text-text-soft-400">—</span>
        ),
    },
    {
      key: 'externalDeliveryUid',
      title: 'Delivery UID',
      render: (_v, row) =>
        row.shipment.externalDeliveryUid ? (
          <span className="font-mono text-[12px] text-text-sub-600 whitespace-nowrap">
            {row.shipment.externalDeliveryUid}
          </span>
        ) : (
          <span className="text-text-soft-400">—</span>
        ),
    },
    {
      key: 'awb',
      title: 'Waybill',
      render: (_v, row) => (
        <span className="font-mono text-[12px] text-text-sub-600 whitespace-nowrap">
          {row.shipment.providerOrderId}
        </span>
      ),
    },
    {
      key: 'bookingId',
      title: 'Booking ID',
      render: (_v, row) =>
        row.shipment.bookingId ? (
          <span className="font-mono text-[12px] text-text-sub-600 whitespace-nowrap">
            {row.shipment.bookingId}
          </span>
        ) : (
          <span className="text-text-soft-400">—</span>
        ),
    },
    {
      key: 'client',
      title: 'Client',
      render: (_v, row) =>
        row.clientName ? (
          <span className="text-xs text-text-sub-600 truncate max-w-[120px] block">
            {row.clientName}
          </span>
        ) : (
          <span className="text-text-soft-400">—</span>
        ),
    },
    {
      key: 'receiverName',
      title: 'Receiver',
      render: (_v, row) => (
        <span className="text-sm text-text-sub-600 truncate max-w-[140px] block">
          {row.shipment.receiverName}
        </span>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      // Condition rides *alongside* the status, never instead of it: a damaged
      // parcel is still somewhere, and the flag follows it to the end.
      render: (_v, row) => (
        <div className="flex flex-wrap items-center gap-1">
          <Badge
            status={ITEM_STATUS_TONE[row.status] ?? 'neutral'}
            type={
              row.status === 'HUB_SCANNED' || row.status === 'HANDOVER_SCANNED'
                ? 'dot'
                : 'default'
            }
            aria-label={`Status: ${ITEM_STATUS_LABEL[row.status] ?? row.status}`}
          >
            {ITEM_STATUS_LABEL[row.status] ?? row.status}
          </Badge>
          {isDamaged(row) && row.status !== 'DAMAGED' && (
            <Badge status="warning" size="sm" aria-label="Condition: damaged">
              Damaged
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'hub',
      title: 'Hub',
      render: (_v, row) => {
        const hub = hubs.find((h) => h.id === Number(row.hubId));
        return (
          <span className="text-xs text-text-sub-600">
            {hub ? hub.name : '—'}
          </span>
        );
      },
    },
    {
      key: 'zoneCode',
      title: 'Zone',
      render: (_v, row) => {
        if (!row.zoneCode) return <span className="text-text-soft-400">—</span>;
        return (
          <div className="flex flex-col leading-tight gap-0.5">
            <span
              className="inline-flex items-center gap-1.5 self-start rounded-lg px-2 py-0.5"
              style={
                row.zoneColor
                  ? { backgroundColor: `${row.zoneColor}18` }
                  : { backgroundColor: 'rgba(94,42,172,0.10)' }
              }
            >
              <span
                className="font-mono text-[11px] font-bold tracking-tight whitespace-nowrap"
                style={
                  row.zoneColor
                    ? { color: row.zoneColor }
                    : { color: '#5E2AAC' }
                }
              >
                {row.zoneCode}
              </span>
            </span>
            {row.zoneName && (
              <span className="text-[10px] text-text-sub-600 truncate max-w-[130px]">
                {row.zoneName}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'scannedAt',
      title: 'Scanned At',
      render: (_v, row) =>
        row.scannedAt ? (
          <span className="font-mono text-[11px] text-text-sub-600 tabular-nums whitespace-nowrap">
            {new Date(row.scannedAt).toLocaleString('id-ID', {
              day: '2-digit',
              month: '2-digit',
              year: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        ) : (
          <span className="text-text-soft-400">—</span>
        ),
    },
    {
      key: 'handedOverAt',
      title: 'Handover At',
      render: (_v, row) =>
        row.handedOverAt ? (
          <span className="font-mono text-[11px] text-text-sub-600 tabular-nums whitespace-nowrap">
            {new Date(row.handedOverAt).toLocaleString('id-ID', {
              day: '2-digit',
              month: '2-digit',
              year: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        ) : (
          <span className="text-text-soft-400">—</span>
        ),
    },
    {
      key: 'createdAt',
      title: 'Created At',
      render: (_v, row) =>
        row.createdAt ? (
          <span className="font-mono text-[11px] text-text-sub-600 tabular-nums whitespace-nowrap">
            {new Date(row.createdAt).toLocaleString('id-ID', {
              day: '2-digit',
              month: '2-digit',
              year: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        ) : (
          <span className="text-text-soft-400">—</span>
        ),
    },
    {
      key: 'actions',
      title: '',
      render: (_v, row) => {
        const busy = exceptionInFlight === row.id;
        // v2: lost is a status, damaged is a condition — either one is a flag
        // that REVERT clears.
        const isFlagged = row.status === 'LOST' || isDamaged(row);
        // Past the hub there is nothing to flag from here; the item is with a
        // rider or already settled, and the BE rejects the write.
        const canFlag = FLAGGABLE_STATUSES.includes(row.status);
        const revertLabel = row.scannedAt
          ? 'Revert to Scanned'
          : 'Revert to Created';

        if (!isFlagged && !canFlag) return null;

        return (
          <div onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <IconButton
                  tone="neutral"
                  style="ghost"
                  size="sm"
                  disabled={busy}
                  aria-label={`Actions for ${row.itemName}`}
                >
                  <i className="ri-more-2-fill text-[18px]" />
                </IconButton>
              </DropdownMenuTrigger>
              {/* Lost (status) and damaged (condition) are independent in v2, so
                  they're offered independently — a damaged parcel can still go
                  missing. REVERT clears whichever flag is set. */}
              <DropdownMenuContent align="end">
                {isFlagged && (
                  <DropdownMenuItem
                    disabled={busy}
                    onSelect={() => handleException(row, 'REVERT')}
                  >
                    <i className="ri-arrow-go-back-line" />
                    {row.status === 'LOST' ? revertLabel : 'Clear damage flag'}
                  </DropdownMenuItem>
                )}
                {canFlag && row.status !== 'LOST' && (
                  <DropdownMenuItem
                    disabled={busy}
                    onSelect={() => handleException(row, 'LOST')}
                  >
                    <i className="ri-error-warning-line" />
                    Mark as Lost
                  </DropdownMenuItem>
                )}
                {canFlag && !isDamaged(row) && (
                  <DropdownMenuItem
                    disabled={busy}
                    onSelect={() => handleException(row, 'DAMAGED')}
                  >
                    <i className="ri-alert-line" />
                    Mark as Damaged
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  // ── Completion state ───────────────────────────────────────────────────────

  const prevTotalRef = useRef(0);
  const showCompletion =
    statusFilter === 'CREATED' &&
    items.length === 0 &&
    !initialLoading &&
    !tableLoading &&
    prevTotalRef.current > 0;

  useEffect(() => {
    if (items.length > 0) prevTotalRef.current = items.length;
  }, [items.length]);

  // ── Render ─────────────────────────────────────────────────────────────────

  const activeFilterCount = countActiveInboundFilters(filters);

  return (
    <>
      <div className="space-y-5 p-6 pb-4">
        {/* Header */}
        <header className="flex flex-col gap-3 border-b border-stroke-soft-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-[28px] font-extrabold tracking-tight text-text-strong-950 leading-none">
              Inbound
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button tone="neutral" style="stroke" onClick={handleRefresh}>
              <i className="ri-refresh-line mr-1.5 text-[14px]" />
              Refresh
            </Button>
            <Button onClick={() => setScanOpen(true)}>
              <i className="ri-qr-scan-line mr-1.5 text-[16px]" />
              Start Scanning
            </Button>
          </div>
        </header>

        {/* Table */}
        <Card padding="none" className="overflow-hidden">
          {initialLoading ? (
            <div className="flex items-center justify-center py-20">
              <Spinner size="lg" />
            </div>
          ) : showCompletion ? (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-(--state-success-dark)">
              <i className="ri-checkbox-circle-fill text-(--state-success-base) text-lg" />
              All items in this batch are scanned — ready for dispatch
            </div>
          ) : (
            <>
              {/* Status tabs + search + filter — same row (matches shipments page) */}
              <div className="flex flex-col gap-3 border-b border-stroke-soft-200 px-4 pt-1 pb-3 lg:flex-row lg:items-end lg:justify-between lg:pb-0">
                {/* Status tabs */}
                <Tabs
                  value={statusFilter}
                  onValueChange={(v) => setStatusFilter(v as StatusFilter)}
                >
                  <TabsList
                    variant="line"
                    className="h-auto min-h-10 w-auto flex-wrap border-0"
                  >
                    {STATUS_TABS.map((tab) => {
                      // v2 counts have no `damaged` bucket (it left the status
                      // axis), so that chip stays blank rather than lying.
                      const count =
                        tab.key === 'ALL'
                          ? counts.all
                          : tab.key === 'DAMAGED'
                            ? null
                            : readItemCount(counts, tab.key);
                      return (
                        <TabsTrigger key={tab.key} value={tab.key}>
                          {tab.label}
                          {!initialLoading && count !== null && (
                            <TabsCount>{count}</TabsCount>
                          )}
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>
                </Tabs>

                {/* Search + Filter — right-aligned */}
                <div className="flex flex-1 items-center justify-end gap-2 pb-2 lg:py-2">
                  <SearchField
                    ref={searchInputRef}
                    value={search}
                    onChange={setSearch}
                    placeholder="Search package, waybill, or delivery ID…"
                    size="sm"
                    className="max-w-xs flex-1"
                  />
                  <InboundFilterPanel
                    open={filterOpen}
                    onOpenChange={handleFilterOpenChange}
                    activeFilterCount={activeFilterCount}
                    value={filterDraft}
                    onChange={setFilterDraft}
                    onApply={applyFilter}
                    onReset={resetFilter}
                  />
                </div>
              </div>

              {/* Scan progress sub-bar — inside table card, below tabs */}
              {!initialLoading && counts.all > 0 && (
                <div className="flex items-center gap-3 border-b border-stroke-soft-200 bg-bg-weak-50/50 px-4 py-1.5">
                  <span className="flex items-center gap-1.5 font-mono text-[11px] text-text-sub-600 tabular-nums">
                    <span className="h-1.5 w-1.5 rounded-full bg-(--state-success-base) flex-shrink-0" />
                    {readItemCount(counts, 'HUB_SCANNED') ?? 0} / {counts.all}{' '}
                    scanned
                  </span>
                  {/* Only lost items are counted here — damaged is a condition
                      and has no status bucket to sum in v2. */}
                  {(counts.lost ?? 0) > 0 && (
                    <span className="font-mono text-[11px] tabular-nums text-(--state-warning-dark)">
                      ⚠ {counts.lost} lost
                    </span>
                  )}
                </div>
              )}
              <div className="p-4">
                <TableData
                  data={items}
                  columns={columns}
                  loading={tableLoading}
                  size="sm"
                  emptyState={
                    <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
                      <i className="ri-inbox-2-line text-3xl text-text-soft-400" />
                      <p className="text-sm font-semibold text-text-sub-600">
                        No inbound items found
                      </p>
                      <p className="text-xs text-text-soft-400">
                        {filters.hubId
                          ? 'No items for this hub yet.'
                          : 'Try adjusting your filters.'}
                      </p>
                    </div>
                  }
                />
              </div>
              {total > DEFAULT_LIMIT && (
                <div className="border-t border-stroke-soft-200 px-4 py-3">
                  <Paginator
                    currentPage={page}
                    totalItems={total}
                    itemsPerPage={DEFAULT_LIMIT}
                    onPageChange={setPage}
                  />
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {/* Scan Panel drawer */}
      <ScanPanel
        isOpen={scanOpen}
        onClose={() => setScanOpen(false)}
        hubs={hubs}
        selectedHubId={filters.hubId ? Number(filters.hubId) : null}
        onHubChange={(id) =>
          writeFilters({ ...filters, hubId: id ? String(id) : '' })
        }
        onScanSuccess={handleScanSuccess}
      />
    </>
  );
};

export default InboundPage;
